// READY TO STORYBOOK
import React from 'react';
import PropTypes from 'prop-types';
import { Switch as SwitchComp } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTheme } from 'contexts/ThemeContext';

import { HelpTooltip, Text } from 'components/DataDisplay';
import Fade from 'components/Fade';
import { Item, Row } from 'components/Layout';

export const IOSSwitch = styled(props => (
  <SwitchComp
    focusVisibleClassName=".Mui-focusVisible"
    disableRipple
    {...props}
  />
))(({ theme, small }) => ({
  width: small ? 28 : 38,
  height: small ? 16 : 20,
  padding: 0,
  margin: '0 5px',
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: small ? 1 : 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#65C466',
        opacity: 1,
        border: 0
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5
      }
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff'
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color:
        theme.palette.mode === 'light'
          ? theme.palette.grey[100]
          : theme.palette.grey[600]
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3
    }
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: small ? 12 : 16,
    height: small ? 12 : 16
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500
    })
  }
}));

export const Switch = props => {
  const {
    checked,
    color,
    onChange,
    size,
    show,
    title,
    required,
    iosStyle,
    tooltip,
    options = [],
    defaultColor,
    ...rest
  } = props;

  const { themeColors } = useTheme();
  const {
    green,
    switchOff,
    inkSoft,
    faint,
    red,
    knobShadow
  } = themeColors.designSystem;

  const switchSx = {
    width: 34,
    height: 19,
    padding: 0,
    margin: '0 7px',
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: '2px',
      color: themeColors.keep_light,
      '&:hover': {
        backgroundColor: 'transparent'
      },
      '&.Mui-checked': {
        transform: 'translateX(15px)',
        color: themeColors.keep_light,
        '&:hover': {
          backgroundColor: 'transparent'
        },
        '& + .MuiSwitch-track': {
          backgroundColor: green,
          opacity: 1,
          border: 0
        }
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5
      }
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 15,
      height: 15,
      boxShadow: knobShadow
    },
    '& .MuiSwitch-track': {
      borderRadius: '20px',
      backgroundColor: switchOff,
      opacity: 1
    }
  };

  const onNext = event => {
    onChange(event.target.checked);
  };

  return (
    <Fade show={show}>
      {title && (
        <Row spacing={2} justify="flex-start" alignItems="center">
          <Item
            justify="flex-start"
            flex
            style={{
              maxWidth: tooltip ? 'calc(100% - 24px)' : null
            }}
          >
            <Text noWrap fontWeight={600} fontSize="11px" color={inkSoft}>
              {title === '&nbsp;' ? <>&nbsp;</> : title}
              {required && (
                <Text component="span" bold color={red}>
                  {' '}
                  *
                </Text>
              )}
            </Text>
          </Item>
          {tooltip && (
            <Item flex style={{ alignItem: 'center' }}>
              <HelpTooltip title={tooltip} />
            </Item>
          )}
        </Row>
      )}
      {options[0] && (
        <Text
          fontSize="12px"
          fontWeight={!checked ? 600 : 400}
          color={!checked ? inkSoft : faint}
        >
          {options[0]}
        </Text>
      )}
      {iosStyle ? (
        <IOSSwitch checked={checked} onChange={onNext} size={size} {...rest} />
      ) : (
        <SwitchComp
          checked={checked}
          sx={switchSx}
          disableRipple
          onChange={onNext}
          size={size}
          color={defaultColor}
          {...rest}
        />
      )}
      {options[1] && (
        <Text
          fontSize="12px"
          fontWeight={checked ? 600 : 400}
          color={checked ? inkSoft : faint}
        >
          {options[1]}
        </Text>
      )}
    </Fade>
  );
};

Switch.defaultProps = {
  checked: null,
  color: null,
  iosStyle: false,
  size: 'medium',
  show: null,
  small: false,
  title: null,
  tootltip: null,
  required: false
};

Switch.propTypes = {
  checked: PropTypes.bool,
  iosStyle: PropTypes.bool,
  small: PropTypes.bool,
  color: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['small', 'medium']),
  show: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['none'])]),
  title: PropTypes.string,
  tootltip: PropTypes.string,
  required: PropTypes.bool
};

export default Switch;
